import type { ReactNode } from 'react';
import { Card, StatusBadge, Spinner } from '@00start/ui';
import type { StageStatus } from '../types';

interface StageCardProps {
  /** 1-based stage number — drives data-testid prefix */
  stage: 1 | 2 | 3;
  title: string;
  status: StageStatus;
  error?: string;
  children: ReactNode;
}

// Maps our local StageStatus to a traffic-light STATUS_COLORS key so the
// StatusBadge dot reads green/amber/red without us hardcoding hex values.
const STATUS_TO_COLOR_KEY: Record<StageStatus, { colorKey: string; label: string }> = {
  idle:    { colorKey: 'expired',     label: 'idle' },
  running: { colorKey: 'reviewing',   label: 'running' },
  ok:      { colorKey: 'approved',    label: 'ok' },
  error:   { colorKey: 'action_req',  label: 'error' },
};

export function StageCard({ stage, title, status, error, children }: StageCardProps) {
  const badge = STATUS_TO_COLOR_KEY[status];

  return (
    <Card
      padding="md"
      header={
        <div
          className="flex items-center justify-between"
          data-testid={`aviation-demo-stage-${stage}-header`}
        >
          <div>
            <div className="text-xs font-bold tracking-wider uppercase text-gray-500 dark:text-slate-400">
              Stage {stage}
            </div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-slate-100">
              {title}
            </h2>
          </div>
          <div
            className="flex items-center gap-2"
            data-testid={`aviation-demo-stage-${stage}-status`}
          >
            {status === 'running' && <Spinner size="sm" />}
            <StatusBadge label={badge.label} colorKey={badge.colorKey} />
          </div>
        </div>
      }
    >
      <div data-testid={`aviation-demo-stage-${stage}-body`}>
        {status === 'error' && error && (
          <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">
            <span className="font-medium">error:</span> {error}
          </p>
        )}
        {children}
      </div>
    </Card>
  );
}
